import FullScreenOverlay from '../../../shared_components/Library/FullScreenOverlay'
import styled from 'styled-components'
import { H3, P, Span } from '../../../shared_components/Library/Typeography'
import { pagePaddingCssRules } from '../../../shared_components/Header/styles'
import { getUnitsUntilTimeElapsed } from '../../../hooks/useCountdownTimer'
import { Chip } from '../../../shared_components/Library/Chip'
import Separator from '../../../shared_components/Library/Separator'
import { useEffect, useRef } from 'react'
import { setBottomNavButton } from '../../../modules/miscUi/actions'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import {
  useAcknowledgeSeasonStartMutation,
  useLazyGetCurrentSeasonProgressQuery,
} from '../../../modules/seasons/api'
import { toast } from 'react-toastify'
import {
  GraduatedMemecoin,
  Season,
  SeasonPrize,
  setCurrentSeasonProgress,
} from '../../../modules/seasons/slice'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectClaimPrizeTxHash,
  selectCurrentSeason,
  selectCurrentSeasonGraduatedMemecoins,
} from '../../../modules/seasons/selectors'
import { Box } from '../../../shared_components/UI/Box'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import { useShouldShowForThreshold } from '../../../theme'
import { TopVideoPostbox } from './Progress'
import { useHistory } from 'react-router-dom'
import { improvedAmountFromWei } from '../../../contexts/StakingContext'
import { selectEthUsdPrice } from '../../../modules/web3/selectors'
import { ClaimedTag } from './SeasonComplete'
import { memecoins } from '../../../modules/memecoins/slice'

const SEASON_RULES = [
  '1% of the supply of all graduated ZAP pools, plus more ZAP goodies are added to the raffle.',
  'Increase your chances of winning the raffle by earning tickets through daily and weekly quests.',
  '5 winners will be chosen this season and will share the prize pool.',
  'Check back as the week progresses to see the rewards accumulate.',
]

export default function AboutThisSeason({
  dismissable,
  isComingSoon,
}: {
  dismissable?: boolean
  isComingSoon?: boolean
  previousSeason?: Season
}) {
  const dispatch = useDispatch()
  const shouldPlayVideo = !dismissable

  const isAtLeastMediumSize = useShouldShowForThreshold('medium')

  useEffect(() => {
    if (!dispatch || isComingSoon) return
    dispatch(
      setBottomNavButton(
        !!dismissable ? 'back_to_season' : 'acknowledge_season_start',
      ),
    )
    return () => {
      if (!dispatch) return
      dispatch(setBottomNavButton(null))
    }
  }, [dispatch, dismissable])

  return (
    <FullScreenOverlay dismissable={dismissable}>
      <Box dc>
        {dismissable && isAtLeastMediumSize && <TopNavBar />}
        <SeasonVideoBanner
          isComingSoon={isComingSoon}
          shouldPlayVideo={shouldPlayVideo}
        />
        <InnerModal g={24} dc>
          <Box dc g={12}>
            <Box jsb ac>
              <H3 s="XL" fw="semibold">
                How it works
              </H3>
              {!isComingSoon && (
                <TimeUntilSeasonEnd isComingSoon={isComingSoon} />
              )}
            </Box>
            <List>
              {SEASON_RULES.map((rule) => (
                <ListItem key={rule}>{rule}</ListItem>
              ))}
            </List>
          </Box>
          <Prizes isComingSoon={isComingSoon} />
        </InnerModal>
      </Box>
    </FullScreenOverlay>
  )
}

export function TopNavBar() {
  const { goBack } = useHistory()
  return (
    <TopNavBarContainer ac>
      <button className="close-button" onClick={goBack}>
        <img src="/img/icons/close.svg" alt="close icon" />
      </button>
    </TopNavBarContainer>
  )
}

const TopNavBarContainer = styled(Box)`
  padding: 16px;
  padding-left: 0;
  padding-right: 0;
  margin-left: -16px;
  margin-bottom: var(--Index-Grids-Gutters);
  .close-button {
    border-radius: 84px;
    border: 1px solid var(--stroke-secondary, #333);
    display: flex;
    width: 40px;
    height: 40px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`

export function SeasonVideoBanner({
  isComingSoon,
  shouldPlayVideo,
  smallOnDesktop,
}: {
  isComingSoon?: boolean
  shouldPlayVideo?: boolean
  smallOnDesktop?: boolean
}) {
  const season = useSelector(selectCurrentSeason)
  const videoRef = useRef<HTMLVideoElement>(null)

  // we've already loaded the video so we can just go to the end :go-horse"
  useEffect(() => {
    if (!!shouldPlayVideo) return
    if (!videoRef.current) return
    const video = videoRef.current
    const goToEnd = () => {
      video.currentTime = video.duration - 0.1
    }
    video.addEventListener('loadeddata', goToEnd)
  }, [videoRef, shouldPlayVideo])

  if (isComingSoon) {
    return (
      <TopVideoPostbox smallOnDesktop={smallOnDesktop}>
        <TopVideoImage src="/img/seasons-coming-soon.svg" />
      </TopVideoPostbox>
    )
  }

  const { videoUrl } = season

  return (
    <TopVideoPostbox smallOnDesktop={smallOnDesktop}>
      <TopVideo autoPlay loop={false} muted ref={videoRef}>
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </TopVideo>
    </TopVideoPostbox>
  )
}

export function TimeUntilSeasonEnd({
  isComingSoon,
}: {
  isComingSoon?: boolean
}) {
  const season = useSelector(selectCurrentSeason)
  let dateToSend = ''
  if (isComingSoon) {
    dateToSend = new Date('2024-12-02T12:00:00.000Z').toISOString()
  } else {
    const { endDate } = season
    dateToSend = new Date(endDate).toISOString()
  }
  const { days, hours, minutes } = getUnitsUntilTimeElapsed(dateToSend)
  const showMinutes = days === '00'
  const formattedString = showMinutes
    ? `${hours}h ${minutes}m`
    : `${days}d ${hours}h`
  return (
    <RawdogChip bgc="surface-minimalPrimary" padV={6} padH={8}>
      <Box fu aw g={4} ac>
        <img
          src="/img/icons/clock.svg"
          alt="clock icon"
          width={16}
          height={16}
        />
        {formattedString}
      </Box>
    </RawdogChip>
  )
}

export function TimeUntilDayEnd() {
  // cap to UTC
  const now = new Date()
  const hours = now.getUTCHours()
  const minutes = now.getUTCMinutes()
  const hoursUntilEnd = 23 - hours
  const minutesUntilEnd = 59 - minutes
  const formattedString = `${hoursUntilEnd}h ${minutesUntilEnd}m`
  return (
    <RawdogChip bgc="surface-minimalPrimary" padV={6} padH={8}>
      <Box fu aw g={4} ac>
        <img
          src="/img/icons/clock-red.svg"
          alt="clock icon"
          width={16}
          height={16}
        />
        {formattedString}
      </Box>
    </RawdogChip>
  )
}

const DUMMY_PRIZES = [
  {
    name: 'ZAP',
    amount: 420,
    symbol: 'ZAP',
    imageUrl: 'https://zap-launchpad.s3.eu-west-2.amazonaws.com/zap-coin.png',
    prizeType: 'ERC20',
    tokenAddress: '0xF56B3b3972F2f154555a0b62FF5A22b7b2A3C90B',
  },
  {
    name: 'NFT',
    amount: 1,
    symbol: 'NFT',
    tokenIds: [1, 2],
    prizeType: 'NFT',
    nftImageUrls: [
      'https://zap-launchpad.s3.eu-west-2.amazonaws.com/nft-1.png',
      'https://zap-launchpad.s3.eu-west-2.amazonaws.com/nft-2.png',
    ],
    tokenAddress: '0x5D165F6004D90758aFd9AD94946d859794B1c4d3',
  },
  {
    name: 'NFT',
    amount: 1,
    symbol: 'NFT',
    tokenIds: [1, 2],
    prizeType: 'NFT',
    nftImageUrls: [
      'https://zap-launchpad.s3.eu-west-2.amazonaws.com/nft-3.png',
      'https://zap-launchpad.s3.eu-west-2.amazonaws.com/nft-4.png',
    ],
    tokenAddress: '0xF28Cd58eAd6199351c6347445ff6388d521e09dF',
  },
]

export function Prizes({
  showPeriodGraduatedCoins = false,
  isComingSoon = false,
  isWinnerScreen = false,
  hasClaimed = false,
}) {
  let prizes: SeasonPrize[] = []
  const season = useSelector(selectCurrentSeason)
  if (!isComingSoon) {
    prizes = season.meta_data.prizes
  } else {
    prizes = DUMMY_PRIZES as SeasonPrize[]
  }

  const isAtLeastMediumSize = useShouldShowForThreshold('medium')
  // TODO: make this factor in previous seasons if showing old season
  const memecoinsThisSeason = useSelector(selectCurrentSeasonGraduatedMemecoins)

  const [nftPrizes, nonNftPrizes]: SeasonPrize[][] = prizes.reduce(
    (acc: SeasonPrize[][], prize: SeasonPrize) => {
      if (prize.prizeType === 'NFT') {
        acc[0].push(prize)
      } else {
        acc[1].push(prize)
      }
      return acc
    },
    [[], []],
  )

  return (
    <Box dc g={12}>
      <H3 s="XL" fw="semibold">
        {isWinnerScreen ? 'Your prizes' : 'Prize pool'}
      </H3>
      {!!isWinnerScreen && !!memecoinsThisSeason.length && (
        <P s="M" c="text-secondary">
          You have won a share of 1% of the supply of all graduated ZAP pools
          this week, plus more ZAP goodies.
        </P>
      )}
      <PrizesBox
        hasClaimed={hasClaimed}
        pad={isComingSoon ? undefined : 'l'}
        dc
        style={{
          marginBottom: isAtLeastMediumSize && isComingSoon ? -42.069 : 0,
        }}
      >
        {!!hasClaimed && <ClaimedTag />}
        {isComingSoon && (
          <ComingSoonBlurAndText c>
            <img src="/img/seasons-confetti.svg" alt="confetti" />
            <H3 s="4XL" fw="bolder" ff="Proxima Nova" ta="center">
              COMING SOON
            </H3>
          </ComingSoonBlurAndText>
        )}
        <Box g={12} dc pad={isComingSoon ? 'l' : undefined}>
          <Box fw g={16}>
            {nonNftPrizes.map((prize, i) => (
              <Prize
                prize={prize}
                key={`non-nft-prize-${i}`}
                isWinnerScreen={isWinnerScreen}
              />
            ))}
          </Box>
          {!!nftPrizes.length && (
            <>
              <Separator />
              <Span s="M" c="text-secondary">
                NFTs
              </Span>
              <Box fw g={16}>
                {nftPrizes.map((prize, i) => (
                  <Prize
                    prize={prize}
                    key={`nft-prize-${i}`}
                    isWinnerScreen={isWinnerScreen}
                  />
                ))}
              </Box>
            </>
          )}
          {!!memecoinsThisSeason.length && <Separator />}
        </Box>
        <GraduatedCoins showPlaceholder={!showPeriodGraduatedCoins} />
      </PrizesBox>
    </Box>
  )
}

const ComingSoonBlurAndText = styled(Box)`
  border-radius: var(--units-12, 12px);
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  backdrop-filter: blur(7.5px);
  z-index: 42069;
  img {
    position: absolute;
    width: 69%;
    height: 69%;
  }
`

function GraduatedCoins({ showPlaceholder }: { showPlaceholder?: boolean }) {
  const isAtLeastMediumSize = useShouldShowForThreshold('medium')
  const winnersThisSeason =
    useSelector(selectCurrentSeason)?.amountOfWinners ?? 0
  const graduatedMemecoins = useSelector(selectCurrentSeasonGraduatedMemecoins)
  const hasClaimed = !!useSelector(selectClaimPrizeTxHash)
  const graduatedCoins: GraduatedMemecoin[] = showPlaceholder
    ? [
        {
          tokenName: '???',
          ethPrice: '$XXX',
          imgUrl: '',
          liquidityPoolAddress: '',
          tokenAddress: '',
        },
        {
          tokenName: '???',
          ethPrice: '$XXX',
          imgUrl: '',
          liquidityPoolAddress: '',
          tokenAddress: '',
        },
        {
          tokenName: '???',
          ethPrice: '$XXX',
          imgUrl: '',
          liquidityPoolAddress: '',
          tokenAddress: '',
        },
      ].slice(0, isAtLeastMediumSize ? 3 : 2)
    : graduatedMemecoins

  const ethUsdPrice = useSelector(selectEthUsdPrice)
  if (!graduatedCoins.length) return <></>

  return (
    <Box dc g={16}>
      <Box dc g={4}>
        <Span s="M" c="text-secondary">
          1% of the supply of all graduated coins on ZAP
        </Span>
        <Span s="S" c="text-secondary" style={{ opacity: 0.6 }}>
          *All coin prices are subject to change
        </Span>
      </Box>
      <Box fw>
        {graduatedCoins.map(({ tokenName, imgUrl, ethPrice }, i: number) => (
          <GraduatedCoinContainer
            key={`graduated-${i}`}
            hasClaimed={hasClaimed}
          >
            <div className="image-container">
              {!!imgUrl && <img src={imgUrl} alt={tokenName} />}
            </div>
            <Box dc g={2}>
              <Span s="L" c="text-primary">
                {tokenName}
              </Span>
              <Span s="S" c="text-secondary">
                $
                {showPlaceholder
                  ? 'XXX'
                  : getUsdValueOfGraduatedCoinPrice(
                      ethPrice,
                      ethUsdPrice,
                      winnersThisSeason,
                    )}
              </Span>
            </Box>
          </GraduatedCoinContainer>
        ))}
      </Box>
    </Box>
  )
}

function getUsdValueOfGraduatedCoinPrice(
  ethPrice: string,
  ethUsdPrice: number,
  numberOfWinners: number,
): string {
  const ethPriceBig = BigInt(ethPrice)
  const usdToEthBig = BigInt(Math.floor(ethUsdPrice))
  const totalWei = ethPriceBig * usdToEthBig
  const usdValue = Number(totalWei) / 10 ** 18
  return Number(
    Number((usdValue * 10_000_000) / numberOfWinners).toFixed(2),
  ).toLocaleString()
}

const GraduatedCoinContainer = styled(Box)<{ hasClaimed?: boolean }>`
  gap: 8px;
  .image-container {
    width: 38px;
    height: 38px;
    border-radius: 88px;
    img {
      border-radius: 88px;
    }
    border: 1px solid var(--stroke-secondary, #333);
    background: var(
      --surface-minimal-alpha-secondary,
      rgba(255, 255, 255, 0.08)
    );
  }
  ${({ hasClaimed }) => hasClaimed && `opacity: 0.4;`}
`

function Prize({ prize, isWinnerScreen = false }) {
  const hasClaimed = !!useSelector(selectClaimPrizeTxHash)

  const amountOfWinners = useSelector(selectCurrentSeason)?.amountOfWinners ?? 0

  const {
    prizeType,
    name,
    amount,
    //tokenAddress,
    imageUrl,
    nftImageUrls,
    tokenIds,
    symbol,
    decimals,
  } = prize

  const isToken = prizeType === 'ERC20'

  // TODO: link to basescan etc
  //let link =
  //prizeType === 'ERC20' ? `https://basescan.org/token/${tokenAddress}` : ''

  const isErc20 = prizeType === 'ERC20'

  const totalPrizeValueString = isErc20
    ? improvedAmountFromWei(amount, decimals).toString()
    : amount.toLocaleString()

  console.log({ amountOfWinners, isWinnerScreen, totalPrizeValueString })

  const formattedAmount = isWinnerScreen
    ? String(Number(totalPrizeValueString) / amountOfWinners)
    : totalPrizeValueString

  if (prizeType === 'NFT') {
    return tokenIds.map((nftTokenId: number, i: number) => (
      <PrizeContainer key={`nft-prize-inner-${i}`} hasClaimed={hasClaimed}>
        <img src={nftImageUrls[i]} alt={name} />
        <Box dc>
          <Span s="XL" fw="semibold">
            #{nftTokenId}
          </Span>
          <Span s="S" c="text-secondary">
            NFT
          </Span>
        </Box>
      </PrizeContainer>
    ))
  }
  return (
    <PrizeContainer hasClaimed={hasClaimed}>
      {' '}
      <img src={imageUrl} alt={name} />
      <Box dc>
        <Span s="XL" fw="semibold">
          {formattedAmount}
        </Span>
        <Span s="S" c="text-secondary">
          {isToken ? `$${symbol.toUpperCase()}` : name}
        </Span>
      </Box>
    </PrizeContainer>
  )
}

export function useClearButtonOnNavigate() {
  const dispatch = useDispatch()
  const history = useHistory()
  useEffect(() => {
    history.listen(() => dispatch(setBottomNavButton(null)))
  }, [dispatch, history])
  return null
}

export type ScrollableModalButtonProps = {
  continueScrolling?: () => void
  moreToScroll?: boolean
}

export function BackToSeasonButton({
  continueScrolling,
  moreToScroll,
}: ScrollableModalButtonProps) {
  const { goBack } = useHistory()
  const canScroll = !!continueScrolling && moreToScroll
  return (
    <ThemedButton
      $styleType="primary"
      w="100%"
      onClick={canScroll ? continueScrolling : goBack}
    >
      {canScroll ? 'Continue' : 'Back to season'}
    </ThemedButton>
  )
}
export function AcknowledgeSeasonStartButton({
  continueScrolling,
  moreToScroll,
}: ScrollableModalButtonProps) {
  const { name: seasonName, id: seasonId } = useSelector(selectCurrentSeason)
  const canScroll = !!continueScrolling && moreToScroll

  const dispatch = useDispatch()

  const [acknowledgeSeasonStart, { isLoading: isLoadingAcknowledge, error }] =
    useAcknowledgeSeasonStartMutation()

  const [
    fetchUpdatedUserProgress,
    {
      isLoading: loadingUpdatedUserSeasonProgress,
      error: userProgressFetchError,
    },
  ] = useLazyGetCurrentSeasonProgressQuery()

  const handleClick = async () => {
    await acknowledgeSeasonStart({ seasonId }).unwrap()
    const { data } = await fetchUpdatedUserProgress({})
    dispatch(setCurrentSeasonProgress(data))
    dispatch(setBottomNavButton(null))
  }

  if (error || userProgressFetchError) {
    toast.error('Failed to proceed to season')
  }

  const isLoading = isLoadingAcknowledge || loadingUpdatedUserSeasonProgress

  return (
    <ThemedButton
      $styleType="primary"
      w="100%"
      onClick={canScroll ? continueScrolling : handleClick}
      disabled={isLoading}
    >
      {canScroll ? (
        'Continue'
      ) : isLoading ? (
        <AnimatedLoader />
      ) : (
        `Continue to ${seasonName}`
      )}
    </ThemedButton>
  )
}

const PrizeContainer = styled(Box)<{ hasClaimed?: boolean }>`
  min-width: calc(50% - 32px);
  img {
    height: 38px;
    width: 38px;
    border-radius: 2px;
  }
  gap: 8px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    min-width: calc(33% - 16px);
  }
  ${({ hasClaimed }) => hasClaimed && `opacity: 0.4;`}
`

const PrizesBox = styled(Box)<{ hasClaimed?: boolean }>`
  border-radius: var(--units-12, 12px);
  border: 1px solid var(--stroke-tertiary, #252525);
  gap: 16px;
  flex-wrap: wrap;
  position: relative;
`

const List = styled.ul`
  padding-left: 16px;
  gap: 4px;
  li:not(:last-child) {
    padding-bottom: 4px;
  }
`

const ListItem = styled.li`
  color: var(--text-secondary, #b1b1b1);
  /* Web/Text/M/Regular */
  font-family: var(--type-family-space-grotesk, 'Space Grotesk');
  font-size: var(--Text-M-M-size, 14px);
  font-style: normal;
  font-weight: var(--type-weight-Regular, 300);
  line-height: var(--Text-M-M-line-height, 18px); /* 128.571% */
  list-style: disc;
`

export const InnerModal = styled(Box)`
  padding-top: 24px !important;
  padding-bottom: 24px !important;
  ${pagePaddingCssRules}
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    padding: 0;
  }
`

// everyone's decided to keep rawdogging instead of
// updating barry so I guess I have permission too :skull"
const RawdogChip = styled(Chip)`
  background:
    linear-gradient(
      0deg,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 0%,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 100%
    ),
    var(--surface-primary, #080808);
  border-radius: var(--units-88, 88px);
  border: 1px solid var(--stroke-tertiary, #252525);
  background:
    linear-gradient(
      0deg,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 0%,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 100%
    ),
    var(--surface-primary, #080808);
  backdrop-filter: blur(40px);
`

const TopVideoImage = styled.img`
  width: 100%;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    border-radius: 12px;
  }
`

export const TopVideo = styled.video`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    border-radius: 12px;
  }
`
